// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { Session } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import validatePasswordHandler from "@insite/client-framework/Store/CommonHandlers/ValidatePassword";
import loadSession from "@insite/client-framework/Store/Context/Handlers/LoadSession";
import resetPassword from "@insite/client-framework/Store/Context/Handlers/ResetPassword";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getHomePageUrl, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { otherJobTitleKey } from "@store/Library/Components/ResetPassword/ResetPasswordJobTitleField";
import useQueryString from "@toolkit/Framework/Common/Hooks/useQueryString";
import { ReactNode, useContext, useEffect, useState } from "react";

const awaitableResetPassword = makeHandlerChainAwaitable(resetPassword);

export interface UseResetPasswordFormOptions {
    /**
     * Forces the mode instead of following the `reset` query parameter.
     */
    mode?: "reset" | "activate";
}

/**
 * Serves both modes. **Resetting** a password is a user who already has an account; **activating**
 * one is a user setting their first password, which additionally captures their Job Title and honours
 * the `targetUrl` the activation link was carrying.
 *
 * Owns every value and the decision of whether the form can submit, so the two screens that use it
 * cannot disagree about it. The field components own only their options and presentation.
 */
const useResetPasswordForm = ({ mode }: UseResetPasswordFormOptions = {}) => {
    const dispatch = useAppDispatch();
    const toasterContext = useContext(ToasterContext);

    const location = useAppSelector(getLocation);
    const homePageUrl = useAppSelector(getHomePageUrl);
    const dashboardUrl = useAppSelector(state => getPageLinkByPageType(state, "MyAccountPage")?.url);
    const { targetUrl } = useQueryString<{ targetUrl?: string }>();

    const [userProfileId, setUserProfileId] = useState<string | undefined>("");
    const [isResettingPasswordFromQuery, setIsResettingPasswordFromQuery] = useState(true);
    const [resetToken, setResetToken] = useState<string | undefined>("");
    const [password, setPassword] = useState("");
    const [passwordErrorMessage, setPasswordErrorMessage] = useState<ReactNode>("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [confirmPasswordErrorMessage, setConfirmPasswordErrorMessage] = useState("");
    const [jobTitles, setJobTitles] = useState<string[]>([]);
    const [jobTitleErrorMessage, setJobTitleErrorMessage] = useState<ReactNode>("");
    const [otherJobTitle, setOtherJobTitle] = useState("");
    const [otherJobTitleErrorMessage, setOtherJobTitleErrorMessage] = useState<ReactNode>("");
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const parsedQuery = parseQueryString<{ userId?: string; resetToken?: string; reset?: string }>(location.search);
        setUserProfileId(parsedQuery.userId);
        setResetToken(parsedQuery.resetToken);
        setIsResettingPasswordFromQuery(parsedQuery.reset?.toLowerCase() === "true");
    }, []);

    const isResettingPassword = mode ? mode === "reset" : isResettingPasswordFromQuery;

    const validatePassword = (password: string): Promise<boolean> => {
        return new Promise(resolve => {
            dispatch(
                validatePasswordHandler({
                    password,
                    onComplete: errorMessage => {
                        setPasswordErrorMessage(errorMessage);
                        resolve(!errorMessage);
                    },
                }),
            );
        });
    };

    const validateConfirmPassword = (newPassword: string, newConfirmPassword: string) => {
        const errorMessage =
            newPassword !== newConfirmPassword
                ? (siteMessage("CreateNewAccountInfo_PasswordCombination_DoesNotMatch") as string)
                : "";
        setConfirmPasswordErrorMessage(errorMessage);
        return !errorMessage;
    };

    const validateJobTitles = (currentJobTitles = jobTitles, currentOtherJobTitle = otherJobTitle) => {
        if (isResettingPassword) {
            return true;
        }

        setJobTitleErrorMessage("");
        setOtherJobTitleErrorMessage("");

        if (!currentJobTitles.length) {
            setJobTitleErrorMessage(siteMessage("CreateNewAccountInfo_MissingValue"));
            return false;
        }

        if (currentJobTitles.includes(otherJobTitleKey) && !currentOtherJobTitle) {
            setOtherJobTitleErrorMessage(siteMessage("CreateNewAccountInfo_MissingOtherRole"));
            return false;
        }

        return true;
    };

    const changePassword = (newPassword: string) => {
        validateConfirmPassword(newPassword, confirmPassword);
        validatePassword(newPassword);
        setPassword(newPassword);
    };

    const changeConfirmPassword = (newConfirmPassword: string) => {
        setConfirmPassword(newConfirmPassword);
        validateConfirmPassword(password, newConfirmPassword);
    };

    const changeJobTitles = (newJobTitles: string[]) => {
        setJobTitles(newJobTitles);
        validateJobTitles(newJobTitles, otherJobTitle);
    };

    const changeOtherJobTitle = (newOtherJobTitle: string) => {
        setOtherJobTitle(newOtherJobTitle);
        validateJobTitles(jobTitles, newOtherJobTitle);
    };

    const submit = async () => {
        setIsSubmitted(true);

        const passwordIsValid = await validatePassword(password);
        const confirmPasswordIsValid = validateConfirmPassword(password, confirmPassword);
        const jobTitleIsValid = validateJobTitles();

        if (!userProfileId || !resetToken || !passwordIsValid || !confirmPasswordIsValid || !jobTitleIsValid) {
            return;
        }

        setIsSubmitting(true);

        let hasError = false;
        await dispatch(
            awaitableResetPassword({
                userProfileId,
                userName: undefined,
                newPassword: password,
                resetToken,
                onError: error => {
                    hasError = true;
                    toasterContext.addToast({ body: error, messageType: "danger" });
                    setIsSubmitting(false);
                },
                // Job titles ride the properties bag, picked up natively by PatchSessionMapper.
                ...{
                    properties: {
                        personas: [...jobTitles, ...(otherJobTitle ? [otherJobTitle] : [])].join("|"),
                    },
                },
            }),
        );

        if (hasError) {
            return;
        }

        /**
         * The backend attaches the authenticated session on password change, so loading it here gives
         * us the new user's landing preference. The destination is then reached with a full page load
         * so every feature and state initialises against that session.
         */
        dispatch(
            loadSession({
                isSessionUpdate: true,
                onComplete: ({ apiResult }: { apiResult: Session }) => {
                    if (!isResettingPassword && !!targetUrl) {
                        window.location.href = targetUrl;
                        return;
                    }

                    window.location.href = (apiResult?.dashboardIsHomepage ? dashboardUrl : homePageUrl) ?? "/";
                },
            }),
        );
    };

    return {
        isResettingPassword,
        password,
        changePassword,
        passwordErrorMessage: isSubmitted ? passwordErrorMessage : "",
        confirmPassword,
        changeConfirmPassword,
        confirmPasswordErrorMessage: isSubmitted ? confirmPasswordErrorMessage : "",
        jobTitles,
        changeJobTitles,
        jobTitleErrorMessage: isSubmitted ? jobTitleErrorMessage : "",
        otherJobTitle,
        changeOtherJobTitle,
        otherJobTitleErrorMessage: isSubmitted ? otherJobTitleErrorMessage : "",
        isSubmitted,
        isSubmitting,
        submit,
    };
};

export default useResetPasswordForm;
